import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import '../styles/FirmaDocumentoPublica.css';

export default function FirmaDocumentoPublica() {
    const { token } = useParams();

    const canvasRef = useRef(null);
    const canvasProfesionalRef = useRef(null);
    const dibujando = useRef(false);
    const [documento, setDocumento] = useState(null);
    const [fase, setFase] = useState("PREVIEW");
    const [isStarted, setIsStarted] = useState(false); // Control para activar fullscreen y bloqueos
    const API_URL = process.env.REACT_APP_API_URL;
    const [profesional, setProfesional] = useState("");
    const tipo = documento?.tipo; 
    const firmaPacienteBase64 = canvasRef.current.toDataURL("image/png");
    const firmaProfesionalBase64 = canvasProfesionalRef.current.toDataURL("image/png");

    const [rgpd, setRgpd] = useState({
        felicitaciones: false,
        comercial: false,
        congresos: false,
        publicaciones: false,
        web: false
    });

    const [datos, setDatos] = useState({
        nombrePaciente: "",
        dniPaciente: "",
        nombreRepresentante: "",
        dniRepresentante: ""
    });

    const profesionalesOptions = [
        { value: "Maria", label: "Maria" },
        { value: "Lucia", label: "Lucia" },
        { value: "Barbara", label: "Barbara" },
        { value: "Alessandro", label: "Alessandro" }
    ];

    /* =======================
       Cargar documento
       ======================= */
    useEffect(() => {
        axios
            .get(`/public/firma/${token}`)
            .then(res => setDocumento(res.data))
            .catch(() => setFase("CANCELADO"));
    }, [token]);

    /* =======================
       Bloqueos de Seguridad
       ======================= */
    useEffect(() => {
        if (!isStarted) return;

        const bloquearAtras = () => {
            window.history.pushState(null, "", window.location.href);
        };

        const bloquearTeclas = (e) => {
            const tag = e.target.tagName?.toLowerCase();
            if (e.key === "Backspace" && tag !== "input" && tag !== "textarea") e.preventDefault();
            if (e.altKey && e.key === "ArrowLeft") e.preventDefault();
            if (e.key === "F5") e.preventDefault();
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "r") e.preventDefault();
        };

        const bloquearSwipe = (e) => {
            if (e.touches && e.touches.length > 1) e.preventDefault();
        };

        // Forzar historial
        window.history.pushState(null, "", window.location.href);
        window.history.pushState(null, "", window.location.href);

        window.addEventListener("popstate", bloquearAtras);
        window.addEventListener("keydown", bloquearTeclas);
        window.addEventListener("touchstart", bloquearSwipe, { passive: false });

        return () => {
            window.removeEventListener("popstate", bloquearAtras);
            window.removeEventListener("keydown", bloquearTeclas);
            window.removeEventListener("touchstart", bloquearSwipe);
        };
    }, [isStarted]);

    const iniciarProcesoSeguro = async () => {
        try {
            if (document.documentElement.requestFullscreen) {
                await document.documentElement.requestFullscreen();
            }
        } catch (e) {
            console.error("Error al activar Fullscreen", e);
        }
        setIsStarted(true);
    };

    /* =======================
       Canvas firma (Original)
       ======================= */
    const start = (e, ref) => {
        dibujando.current = true;

        const rect = ref.current.getBoundingClientRect();
        const ctx = ref.current.getContext("2d");

        ctx.beginPath();
        ctx.moveTo(
            (e.touches ? e.touches[0].clientX : e.clientX) - rect.left,
            (e.touches ? e.touches[0].clientY : e.clientY) - rect.top
        );
    };

    const draw = (e, ref) => {
        if (!dibujando.current) return;

        const rect = ref.current.getBoundingClientRect();
        const ctx = ref.current.getContext("2d");

        ctx.lineTo(
            (e.touches ? e.touches[0].clientX : e.clientX) - rect.left,
            (e.touches ? e.touches[0].clientY : e.clientY) - rect.top
        );

        ctx.stroke();
    };

    const end = () => { dibujando.current = false; };
    const limpiarFirma = ref => {
        const ctx = ref.current.getContext("2d");

        ctx.clearRect(
            0,
            0,
            ref.current.width,
            ref.current.height
        );
    };

    /* =======================
       Acciones
       ======================= */
    const confirmarFirmaPaciente = () => {
        setFase("FIRMA_PROFESIONAL");
    };

    const confirmarFirmaCompleta = async () => {
        const firmaBase64 = canvasRef.current.toDataURL("image/png");
        await axios.post(`/public/firma/${token}`, {
            firmaBase64: firmaPacienteBase64,
            firmaProfesionalBase64,
            profesional,
            dispositivo: navigator.userAgent,
            nombrePaciente: datos.nombrePaciente,
            dniPaciente: datos.dniPaciente,
            nombreRepresentante: datos.nombreRepresentante,
            dniRepresentante: datos.dniRepresentante,
            consentimientoProteccionDatos: {
                felicitacionesNavidad: rgpd.felicitaciones,
                infoComercial: rgpd.comercial,
                congresos: rgpd.congresos,
                publicacionesCientificas: rgpd.publicaciones,
                webRedes: rgpd.web
            }
        });

        window.open(`/public/firma/${token}/pdf-firmado`, "_blank");
        setFase("CONFIRMADO");
    };

    const cancelarFirma = () => { setFase("CANCELADO"); };

    if (!documento) return <p>Cargando documento…</p>;

    // PANTALLA DE ACCESO (Mantiene Fullscreen en Tablet)
    if (!isStarted) {
        return (
            <div className="flex flex-col items-center justify-center h-screen text-center" style={{ backgroundColor: '#f3f4f6' }}>
                <h2 style={{ marginBottom: '1rem' }}>Preparando documento para firma</h2>
                <button 
                    onClick={iniciarProcesoSeguro}
                    className="boton-firmar"
                    style={{ padding: '15px 30px', fontSize: '1.2rem' }}
                >
                    Comenzar ahora
                </button>
            </div>
        );
    }

    return (
        <div style={{ padding: 20, maxWidth: 900, margin: "0 auto" }}>
            <h3>{documento.nombreDocumento}</h3>

            {/* PDF SOLO LECTURA */}
            {fase !== "CANCELADO" && (
                <iframe
                    title="Documento"
                    src={`/public/firma/${token}/pdf`}
                    style={{ width: "100%", height: 600, border: "1px solid #ccc" }}
                />
            )}

            {/* DATOS EDITABLES */}
            {fase === "PREVIEW" && (
                <div className="firma-formulario">
                    <div style={{ marginTop: 20 }}>
                        <h4>Datos para el consentimiento</h4>
                        <div style={{ display: "grid", gap: 10 }}>
                            {tipo !== "CONSENTIMIENTO_PROTECCION_DATOS" && (
                                <> 
                                    <input
                                        placeholder="Nombre del paciente"
                                        value={datos.nombrePaciente}
                                        onChange={e => setDatos({ ...datos, nombrePaciente: e.target.value })}
                                    />
                                    <input
                                        placeholder="DNI del paciente"
                                        value={datos.dniPaciente}
                                        onChange={e => setDatos({ ...datos, dniPaciente: e.target.value })}
                                    />
                                </>
                            )}
                            <input
                                placeholder="Representante legal (si procede)"
                                value={datos.nombreRepresentante}
                                onChange={e => setDatos({ ...datos, nombreRepresentante: e.target.value })}
                            />
                            <input
                                placeholder="DNI representante"
                                value={datos.dniRepresentante}
                                onChange={e => setDatos({ ...datos, dniRepresentante: e.target.value })}
                            />                            
                            <select
                                value={profesional}
                                onChange={e => setProfesional(e.target.value)}
                            >
                                <option value="">Seleccione profesional</option>

                                {profesionalesOptions.map(p => (
                                    <option key={p.value} value={p.value}>
                                        {p.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {tipo === "CONSENTIMIENTO_PROTECCION_DATOS" && (
                            <div className="rgpd-checks">
                                <label><input type="checkbox" checked={rgpd.felicitaciones} onChange={e => setRgpd({ ...rgpd, felicitaciones: e.target.checked })} /> Envío de felicitaciones navideñas</label>
                                <label><input type="checkbox" checked={rgpd.comercial} onChange={e => setRgpd({ ...rgpd, comercial: e.target.checked })} /> Envío de información comercial</label>
                                <label><input type="checkbox" checked={rgpd.congresos} onChange={e => setRgpd({ ...rgpd, congresos: e.target.checked })} /> Uso de imágenes para congresos</label>
                                <label><input type="checkbox" checked={rgpd.publicaciones} onChange={e => setRgpd({ ...rgpd, publicaciones: e.target.checked })} /> Uso de imágenes en publicaciones científicas</label>
                                <label><input type="checkbox" checked={rgpd.web} onChange={e => setRgpd({ ...rgpd, web: e.target.checked })} /> Uso de imágenes en web o redes sociales</label>
                            </div>                      
                        )}

                        {tipo === "CONSENTIMIENTO_PROTECCION_DATOS" && (
                            <p className="rgpd-declaracion">
                                Declaro haber recibido la información sobre protección de datos y comprender el tratamiento que se realizará sobre mis datos personales.
                            </p>
                        )}

                        <div className="botones-firma" style={{ marginTop: 20 }}>
                            <button className="boton-firmar" onClick={() => setFase("FIRMA_PACIENTE")}>
                                Firmar documento
                            </button>
                            <button className="boton-cancelarFirma" onClick={cancelarFirma} style={{ marginLeft: 10 }}>
                                Cancelar
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* FIRMA_PACIENTE */}
            {fase === "FIRMA_PACIENTE" && (
                <>
                    <p style={{ marginTop: 20 }}>Firme dentro del recuadro</p>
                    <canvas
                        ref={canvasRef}
                        width={700}
                        height={200}
                        style={{ border: "2px solid #000", touchAction: "none" }}
                        onMouseDown={e => start(e, canvasRef)}
                        onMouseMove={e => draw(e, canvasRef)}
                        onMouseUp={end}
                        onMouseLeave={end}

                        onTouchStart={e => start(e, canvasRef)}
                        onTouchMove={e => draw(e, canvasRef)}
                        onTouchEnd={end}
                    />
                    <div style={{ marginTop: 15 }}>
                        <button className="boton-firmar" onClick={confirmarFirmaPaciente}>Confirmar firma</button>
                        <button className="boton-limpiar" onClick={() => limpiarFirma(canvasRef)} style={{ marginLeft: 10 }}>Limpiar</button>
                        <button className="boton-cancelarFirma" onClick={cancelarFirma} style={{ marginLeft: 10 }}>Cancelar</button>
                    </div>
                </>
            )}
            {fase === "FIRMA_PROFESIONAL" && (
                <>
                    <h3>Firma del profesional</h3>

                    <p>
                        Profesional: <strong>{profesional}</strong>
                    </p>

                    <p>Firme dentro del recuadro</p>

                    <canvas
                        ref={canvasProfesionalRef}
                        width={700}
                        height={200}
                        style={{ border: "2px solid #000", touchAction: "none" }}
                        onMouseDown={e => start(e, canvasProfesionalRef)}
                        onMouseMove={e => draw(e, canvasProfesionalRef)}

                        onTouchStart={e => start(e, canvasProfesionalRef)}
                        onTouchMove={e => draw(e, canvasProfesionalRef)}
                        onTouchEnd={end}
                    />

                    <div style={{ marginTop: 15 }}>
                        <button
                            className="boton-firmar"
                            onClick={confirmarFirmaCompleta}
                        >
                            Finalizar firma
                        </button>

                        <button
                            className="boton-limpiar"
                            onClick={() => limpiarFirma(canvasProfesionalRef)}
                            style={{ marginLeft: 10 }}
                        >
                            Limpiar
                        </button>

                        <button
                            className="boton-cancelarFirma"
                            onClick={cancelarFirma}
                            style={{ marginLeft: 10 }}
                        >
                            Cancelar
                        </button>
                    </div>
                </>
            )}
            {fase === "CONFIRMADO" && <h4 style={{ color: "green", marginTop: 20 }}>Documento firmado correctamente</h4>}
            {fase === "CANCELADO" && <h4 style={{ color: "red", marginTop: 20 }}>Firma cancelada</h4>}
        </div>
    );
}